import React, { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    company_name: '',
    company_tagline: '',
    company_logo: '',
    company_logo_text: '',
    phone: '',
    whatsapp_number: '',
    email: '',
    address: '',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    hero_title: '',
    hero_subtitle: '',
    hero_button_text: '',
    hero_button_link: '',
    footer_text: '',
    business_hours: '',
    support_24_7: '',
    // Map settings
    map_latitude: '-1.9441',
    map_longitude: '30.0619',
    map_zoom: '13',
    map_marker_title: 'HYPA MOTORS HQ',
    map_address: 'Kigali, Rwanda',
    map_enabled: 'true',
    google_maps_api_key: ''
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [savingFields, setSavingFields] = useState({});
  const [logoUseUrlInput, setLogoUseUrlInput] = useState(true);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value) => {
    const token = localStorage.getItem('adminToken');
    
    setSavingFields(prev => ({ ...prev, [key]: 'saving' }));
    
    try {
      await axios.put(`${API_BASE_URL}/api/admin/settings/${key}`, 
        { value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavingFields(prev => ({ ...prev, [key]: 'saved' }));
      setTimeout(() => {
        setSavingFields(prev => ({ ...prev, [key]: null }));
      }, 1500);
    } catch (error) {
      console.error('Error updating setting:', error);
      setSavingFields(prev => ({ ...prev, [key]: 'error' }));
      setTimeout(() => {
        setSavingFields(prev => ({ ...prev, [key]: null }));
      }, 2000);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    updateSetting(key, value);
  };

  const handleLogoChange = async (value, isFile = false) => {
    if (isFile) {
      const file = value;
      if (!file) return;
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);
      try {
        const token = localStorage.getItem('adminToken');
        const response = await axios.post(`${API_BASE_URL}/api/admin/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.data.success) {
          handleChange('company_logo', response.data.imageUrl);
        }
      } catch (error) {
        console.error('Upload error:', error);
        alert('Failed to upload logo');
      } finally {
        setUploading(false);
      }
    } else {
      handleChange('company_logo', value);
    }
  };

  const renderField = (label, key, type = 'text', placeholder = '') => {
    const value = settings[key] || '';
    const status = savingFields[key];
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-1">{label}</label>
        <div className="relative">
          {type === 'textarea' ? (
            <textarea
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              rows="3"
              className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white pr-20"
              placeholder={placeholder}
            />
          ) : (
            <input
              type={type}
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
              className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white pr-20"
              placeholder={placeholder}
            />
          )}
          
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            {status === 'saving' && (
              <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            )}
            {status === 'saved' && (
              <span className="text-green-500 text-xs">✓ Saved</span>
            )}
            {status === 'error' && (
              <span className="text-red-500 text-xs">✗ Error</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6">Website Settings</h1>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-dark-700">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'general'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          General
        </button>
        <button
          onClick={() => setActiveTab('contact')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'contact'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Contact & Social
        </button>
        <button
          onClick={() => setActiveTab('hero')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'hero'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Hero Section
        </button>
        <button
          onClick={() => setActiveTab('footer')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'footer'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Footer
        </button>
        <button
          onClick={() => setActiveTab('map')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'map'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Map Settings
        </button>
        <button
  onClick={() => setActiveTab('about')}
  className={`px-4 py-2 font-medium transition ${
    activeTab === 'about'
      ? 'text-orange-500 border-b-2 border-orange-500'
      : 'text-gray-400 hover:text-gray-300'
  }`}
>
  About Page
</button>
      </div>

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <h2 className="text-lg font-bold text-white mb-4">Company Information</h2>
          
          <div className="mb-6 border-b border-dark-700 pb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">Company Logo</label>
            <div className="flex space-x-2 mb-3">
              <button
                type="button"
                onClick={() => setLogoUseUrlInput(true)}
                className={`flex-1 py-2 rounded-lg text-sm ${
                  logoUseUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'
                }`}
              >
                Use URL
              </button>
              <button
                type="button"
                onClick={() => setLogoUseUrlInput(false)}
                className={`flex-1 py-2 rounded-lg text-sm ${
                  !logoUseUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'
                }`}
              >
                Upload File
              </button>
            </div>

            {logoUseUrlInput ? (
              <input
                type="text"
                value={settings.company_logo || ''}
                onChange={(e) => handleLogoChange(e.target.value, false)}
                className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                placeholder="https://example.com/logo.png"
              />
            ) : (
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleLogoChange(e.target.files[0], true)}
                className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white file:mr-2 file:py-1 file:px-3 file:rounded file:bg-orange-500 file:text-white file:border-none"
                disabled={uploading}
              />
            )}
            {uploading && <p className="text-gray-400 text-sm mt-1">Uploading...</p>}
            {settings.company_logo && (
              <img src={settings.company_logo} alt="Logo Preview" className="mt-3 h-12 w-auto object-contain border border-dark-600 rounded p-1 bg-white" />
            )}
          </div>

          {renderField('Company Name', 'company_name', 'text', 'HYPA MOTORS')}
          {renderField('Company Tagline', 'company_tagline', 'text', 'Drive in Luxury')}
          {renderField('Logo Text (fallback)', 'company_logo_text', 'text', 'HM')}
        </div>
      )}

      {/* Contact & Social Tab */}
      {activeTab === 'contact' && (
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <h2 className="text-lg font-bold text-white mb-4">Contact Information</h2>
          
          {renderField('Phone Number', 'phone', 'tel', '+250 783 044 172')}
          {renderField('WhatsApp Number', 'whatsapp_number', 'tel', '+250 783 044 172')}
          {renderField('Email Address', 'email', 'email', 'info@hypamotors.com')}
          {renderField('Address', 'address', 'text', 'Kigali, Rwanda')}
          {renderField('Business Hours', 'business_hours', 'textarea', 'Monday - Friday: 8:00 - 18:00')}
          {renderField('24/7 Support Text', 'support_24_7', 'text', '24/7 Customer Support')}

          <h2 className="text-lg font-bold text-white mt-6 mb-4">Social Media Links</h2>
          
          {renderField('Facebook URL', 'facebook_url', 'url', 'https://facebook.com/hypamotors')}
          {renderField('Twitter URL', 'twitter_url', 'url', 'https://twitter.com/hypamotors')}
          {renderField('Instagram URL', 'instagram_url', 'url', 'https://instagram.com/hypamotors')}
        </div>
      )}

      {/* Hero Section Tab */}
      {activeTab === 'hero' && (
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <h2 className="text-lg font-bold text-white mb-4">Hero Section</h2>
          
          {renderField('Hero Title', 'hero_title', 'text', 'Experience the Ultimate SUV')}
          {renderField('Hero Subtitle', 'hero_subtitle', 'text', 'Drive in Luxury with HYPA MOTORS')}
          {renderField('Button Text', 'hero_button_text', 'text', 'Book a Car')}
          {renderField('Button Link', 'hero_button_link', 'text', '/cars')}
        </div>
      )}

      {/* Footer Tab */}
      {activeTab === 'footer' && (
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <h2 className="text-lg font-bold text-white mb-4">Footer Settings</h2>
          
          {renderField('Footer Text', 'footer_text', 'textarea', '© 2024 HYPA MOTORS. All rights reserved.')}
        </div>
      )}
      {/* About Page Tab */}
{activeTab === 'about' && (
  <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
    <h2 className="text-lg font-bold text-white mb-4">About Page Settings</h2>
    
    {renderField('Page Title', 'about_title', 'text', 'About HYPA MOTORS')}
    {renderField('Page Subtitle', 'about_subtitle', 'text', 'Your Trusted Partner for Luxury Travel in Rwanda')}
    {renderField('Main Description', 'about_description', 'textarea', '')}
    {renderField('Mission Statement', 'about_mission', 'textarea', '')}
    {renderField('Vision Statement', 'about_vision', 'textarea', '')}
    {renderField('Hero Image URL', 'about_image', 'text', 'https://example.com/image.jpg')}

    <div className="mt-4">
      <label className="flex items-center space-x-3">
        <input
          type="checkbox"
          checked={settings.about_page_enabled === 'true'}
          onChange={(e) => handleChange('about_page_enabled', String(e.target.checked))}
          className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-orange-500 focus:ring-orange-500"
        />
        <span className="text-sm text-gray-300">Enable About Page</span>
      </label>
    </div>
  </div>
)}

      {/* Map Settings Tab */}
      {activeTab === 'map' && (
        <div className="bg-dark-900 rounded-lg p-6 border border-dark-700">
          <h2 className="text-lg font-bold text-white mb-4">Google Map Settings</h2>
          
          <div className="mb-6 bg-dark-800 rounded-lg p-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Map Preview</label>
            <div className="h-64 rounded-lg overflow-hidden border border-dark-600">
              <iframe
                title="Map Preview"
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src={`https://staticmap.openstreetmap.de/staticmap.php?center=${settings.map_latitude || '-1.9441'},${settings.map_longitude || '30.0619'}&zoom=${settings.map_zoom || '13'}&size=600x300&maptype=mapnik`}
                allowFullScreen
              ></iframe>
            </div>
            <p className="text-xs text-gray-500 mt-2">Preview of your map location</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {renderField('Latitude', 'map_latitude', 'text', '-1.9441')}
            {renderField('Longitude', 'map_longitude', 'text', '30.0619')}
            {renderField('Zoom Level (1-20)', 'map_zoom', 'number', '13')}
            {renderField('Marker Title', 'map_marker_title', 'text', 'HYPA MOTORS HQ')}
            {renderField('Address Text', 'map_address', 'text', 'Kigali, Rwanda')}
          </div>

          <div className="mt-4">
            {renderField('Google Maps API Key (Optional)', 'google_maps_api_key', 'text', 'YOUR_API_KEY')}
            <p className="text-xs text-gray-500 mt-1">
              Get a free API key from <a href="https://developers.google.com/maps/documentation/embed/get-api-key" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:underline">Google Cloud Console</a>. Leave empty to use OpenStreetMap.
            </p>
          </div>

          <div className="mt-4">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={settings.map_enabled === 'true'}
                onChange={(e) => handleChange('map_enabled', String(e.target.checked))}
                className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm text-gray-300">Enable Map on Contact Page</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;